import React, { FC, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Dayjs } from 'dayjs';
import axiosInstance from '../../api/axios';
import { paymentService } from '../../api/payment';
import TransactionTable, { Transaction } from './TransactionTable';
import TransactionFilters from './TransactionFilters';
import Pagination from '../pagination/pagination';
import { useAppDispatch } from 'store/hooks';
import { showToast } from 'store/toastSlice';
import Modal from '../modal/modal';
import styles from './transactions.module.scss';

const Transactions: FC = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'Deposit' | 'Withdrawal' | 'Tip'>('Deposit');
  const [cancelModal, setCancelModal] = useState<{ open: boolean; txId: string | null }>({
    open: false,
    txId: null,
  });
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [fromDate, setFromDate] = useState<Dayjs | null>(null);
  const [toDate, setToDate] = useState<Dayjs | null>(null);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    page: 1,
    status: '',
    type: '',
    minAmount: '',
    maxAmount: '',
  });

  const [isFiltersOpen, setIsFiltersOpen] = useState(false);

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const endpoint =
        activeTab === 'Deposit'
          ? '/client/payment/deposit-transactions'
          : activeTab === 'Withdrawal'
            ? '/client/payment/withdraw-transactions'
            : '/client/balance/tip-transactions';

      const params = new URLSearchParams();
      params.append('page', filters.page.toString());
      params.append('limit', '10');
      if (activeTab === 'Tip' && filters.type) params.append('type', filters.type);
      if (activeTab !== 'Tip' && filters.status) params.append('status', filters.status);

      if (fromDate) {
        params.append('dateFrom', fromDate.startOf('day').toISOString());
      }
      if (toDate) {
        params.append('dateTo', toDate.endOf('day').toISOString());
      }

      if (filters.minAmount) params.append('minAmount', filters.minAmount);
      if (filters.maxAmount) params.append('maxAmount', filters.maxAmount);

      const response = await axiosInstance.get(`${endpoint}?${params.toString()}`);
      setTransactions(response.data.data?.docs || []);
      setTotalPages(response.data.data?.totalPages || 1);
    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setLoading(false);
    }
  }, [activeTab, filters, fromDate, toDate]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const handleTabChange = (tab: 'Deposit' | 'Withdrawal' | 'Tip') => {
    if (activeTab === tab) return;
    setActiveTab(tab);
    setFilters({
      page: 1,
      status: '',
      type: '',
      minAmount: '',
      maxAmount: '',
    });
    setFromDate(null);
    setToDate(null);
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    if (name === 'minAmount' || name === 'maxAmount') {
      if (value !== '' && !/^\d*\.?\d*$/.test(value)) {
        return;
      }
    }

    setFilters((prev) => ({ ...prev, [name]: value, page: 1 }));
  };

  const handleCancelClick = (txId: string) => {
    setCancelModal({ open: true, txId });
  };

  const confirmCancel = async () => {
    if (!cancelModal.txId) return;
    setCancellingId(cancelModal.txId);
    try {
      await paymentService.cancelWithdrawal(cancelModal.txId);
      dispatch(
        showToast({
          text: t('transactions.toasts.cancelSuccess', 'Withdrawal cancelled successfully'),
        }),
      );
      setTransactions((prev) =>
        prev.map((tx) => (tx.id === cancelModal.txId ? { ...tx, status: 'cancelled' } : tx)),
      );
    } catch (error: any) {
      dispatch(
        showToast({
          text:
            error.response?.data?.message ||
            t('transactions.toasts.cancelFailed', 'Failed to cancel withdrawal'),
        }),
      );
    } finally {
      setCancellingId(null);
      setCancelModal({ open: false, txId: null });
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.tabs}>
        <button
          className={`${styles.tab} ${activeTab === 'Deposit' ? styles.active : ''}`}
          onClick={() => handleTabChange('Deposit')}
        >
          {t('transactions.tabs.deposit', 'Deposit')}
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'Withdrawal' ? styles.active : ''}`}
          onClick={() => handleTabChange('Withdrawal')}
        >
          {t('transactions.tabs.withdrawal', 'Withdrawal')}
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'Tip' ? styles.active : ''}`}
          onClick={() => handleTabChange('Tip')}
        >
          {t('transactions.tabs.tip', 'Tip')}
        </button>
      </div>

      <div className={styles.filterToggle} onClick={() => setIsFiltersOpen(!isFiltersOpen)}>
        <div className={styles.toggleLeft}>
          <svg
            viewBox="0 0 24 24"
            width="20"
            height="20"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="4" y1="6" x2="20" y2="6"></line>
            <line x1="6" y1="12" x2="18" y2="12"></line>
            <line x1="9" y1="18" x2="15" y2="18"></line>
          </svg>
          <span>{t('transactions.filters.toggle', 'Filters')}</span>
        </div>
        <svg
          className={`${styles.arrow} ${isFiltersOpen ? styles.open : ''}`}
          viewBox="0 0 24 24"
          width="18"
          height="18"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </div>

      <div className={`${styles.filters} ${isFiltersOpen ? styles.isOpen : ''}`}>
        <div className={styles.innerFilters}>
          <TransactionFilters
            activeTab={activeTab}
            filters={filters}
            setFilters={setFilters}
            fromDate={fromDate}
            setFromDate={setFromDate}
            toDate={toDate}
            setToDate={setToDate}
            handleFilterChange={handleFilterChange}
          />
        </div>
      </div>

      <TransactionTable
        transactions={transactions}
        loading={loading}
        activeTab={activeTab}
        onCancelClick={handleCancelClick}
        isCancellingId={cancellingId}
      />

      <Modal
        isOpen={cancelModal.open}
        onClose={() => setCancelModal({ open: false, txId: null })}
        title={t('transactions.modal.cancelTitle', 'Cancel Withdrawal')}
      >
        <p style={{ marginBottom: '24px', color: '#ccc' }}>
          {t('transactions.modal.cancelPrompt', 'Are you sure you want to cancel this withdrawal?')}
        </p>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            onClick={() => setCancelModal({ open: false, txId: null })}
            disabled={!!cancellingId}
            style={{
              padding: '10px 20px',
              borderRadius: '8px',
              border: '1px solid #444',
              background: 'transparent',
              color: '#fff',
              cursor: 'pointer',
            }}
          >
            {t('transactions.actions.keepIt', 'No, keep it')}
          </button>
          <button
            onClick={confirmCancel}
            disabled={!!cancellingId}
            style={{
              padding: '10px 20px',
              borderRadius: '8px',
              background: '#e74c3c',
              color: 'white',
              border: 'none',
              cursor: 'pointer',
              opacity: cancellingId ? 0.7 : 1,
            }}
          >
            {cancellingId
              ? t('transactions.actions.cancelling', 'Cancelling...')
              : t('transactions.actions.confirmCancel', 'Yes, cancel')}
          </button>
        </div>
      </Modal>

      {!loading && transactions.length > 0 && (
        <div className={styles.paginationWrapper}>
          <Pagination
            currentPage={filters.page}
            totalPages={totalPages}
            onPageChange={(page) => setFilters((prev) => ({ ...prev, page }))}
          />
        </div>
      )}
    </div>
  );
};

export default Transactions;
